/**
 * THE IN-GAME BUG REPORTER. One button, present on every screen, that carries the state with it.
 *
 * 🚨 **REBUILT 2026-08-17 BECAUSE IT HAD BEEN SILENTLY LOST.** The original lived in
 * `web/bug-reporter.html` and was injected into the UNITY shell's body by `deploy.ps1`. The product
 * became `client-web` and it did not come across. Measured on the live host: no button in the page,
 * nothing in the bundle, and `POST /api/bug` answering **404**. `ui/betamark.ts` was still telling
 * testers that reporting a bug was the most useful thing they could do, with no way to do it, on a
 * beta whose entire value is feedback from about a dozen real people.
 *
 * ⭐ **IT COPIES THE STATE AND THE PAGE. THE PLAYER DOES NOT DESCRIBE THEM.** Alex, 2026-08-17:
 * *"The in game bug reporter should copy the state and page the game is on when a user clicks and
 * fills it in."* A tester writing "it broke on the board" costs a session of guessing; the same
 * report carrying the screen id, the turn, the timer state, the unhandled engine events and the
 * last JavaScript error is usually the diagnosis. `app/diagnostics.ts` owns the collection.
 *
 * ⚠️ **THE CONTEXT IS GATHERED ON OPEN, NOT ON SUBMIT.** A player who opens the reporter has just
 * seen the problem. By the time they finish typing they may have navigated, the board may have been
 * torn down, and a timer may have moved the game on - so a snapshot taken at submit describes the
 * wrong moment. This is the one ordering decision in the file and it is the whole point of it.
 *
 * ⚠️ **AND IT SHOWS THEM WHAT IT IS SENDING.** A diagnostic bundle collected invisibly and posted to
 * a server is a thing we should not do to players without showing it. The panel folds the payload
 * open on request; nothing is sent until they press the button.
 */
import { Api } from "../net/api";
import { snapshot } from "../app/diagnostics";

let installed = false;

export function installBugReporter(): void {
  // ⚠️ Idempotent. `main.ts` boots once today, but a reporter that stacks two buttons on a re-entry
  // is the kind of defect that only shows up in the screenshot of the bug somebody is reporting.
  if (installed) return;
  installed = true;

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "bugbtn";
  btn.dataset["label"] = "REPORT A BUG";
  btn.textContent = "REPORT A BUG";
  btn.setAttribute("aria-haspopup", "dialog");

  btn.addEventListener("click", () => openPanel());
  document.body.append(btn);
}

function openPanel(): void {
  if (document.querySelector(".bugpanel")) return;

  // ⭐ TAKEN HERE. See the header: the moment of OPENING is the moment the player saw the problem.
  const snap = snapshot();

  const root = document.createElement("div");
  root.className = "bugpanel";
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.setAttribute("aria-label", "Report a bug");

  const card = document.createElement("div");
  card.className = "bugpanel__card";

  const h = document.createElement("h2");
  h.className = "bugpanel__title";
  h.textContent = "REPORT A BUG";

  const sub = document.createElement("p");
  sub.className = "bugpanel__sub";
  sub.textContent = `We have already attached where you are (${snap.screen || "unknown screen"}) `
                  + "and what the game was doing. Just tell us what went wrong.";

  const ta = document.createElement("textarea");
  ta.className = "bugpanel__text";
  ta.rows = 5;
  ta.placeholder = "What happened? What did you expect instead?";
  ta.setAttribute("aria-label", "What went wrong");

  const contact = document.createElement("input");
  contact.className = "bugpanel__contact";
  contact.type = "text";
  contact.placeholder = "Discord or email, if you want a reply (optional)";
  contact.setAttribute("aria-label", "Contact, optional");

  // ⭐ SHOW THE PAYLOAD. Folded away, because most players will not care, but never hidden.
  const details = document.createElement("details");
  details.className = "bugpanel__details";
  const summary = document.createElement("summary");
  summary.textContent = "What gets sent with this";
  const pre = document.createElement("pre");
  pre.className = "bugpanel__payload";
  pre.textContent = JSON.stringify(snap, null, 2);
  details.append(summary, pre);

  const say = document.createElement("p");
  say.className = "bugpanel__say";
  say.setAttribute("role", "status");

  const row = document.createElement("div");
  row.className = "bugpanel__row";
  const send = plate("SEND REPORT");
  const cancel = plate("CANCEL", true);
  row.append(send, cancel);

  const close = () => {
    window.removeEventListener("keydown", onKey);
    root.remove();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  window.addEventListener("keydown", onKey);
  cancel.addEventListener("click", close);

  send.addEventListener("click", () => {
    const words = ta.value.trim();
    if (!words) {
      say.textContent = "Tell us what went wrong first.";
      ta.focus();
      return;
    }
    send.disabled = true;
    say.textContent = "Sending.";
    void (async () => {
      try {
        await Api.reportBug({ message: words, contact: contact.value.trim(), snapshot: snap });
        say.textContent = "Sent. Thank you, this is genuinely the most useful thing you can do.";
        send.remove();
        (cancel.querySelector(".plate__label") as HTMLElement | null)?.replaceChildren("CLOSE");
      } catch (err) {
        // ⚠️ NEVER SILENT. A reporter that fails quietly teaches a tester their reports vanish, and
        // they stop sending them - which costs far more than the one lost report.
        say.textContent = "That did not send. Please try again in a moment.";
        send.disabled = false;
        console.error("[bug] report failed", err);
      }
    })();
  });

  card.append(h, sub, ta, contact, details, row, say);
  root.append(card);
  document.body.append(root);
  ta.focus();
}

function plate(label: string, quiet = false): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = quiet ? "plate plate--quiet" : "plate";
  b.dataset["label"] = label;
  const face = document.createElement("span");
  face.className = "plate__face";
  face.setAttribute("aria-hidden", "true");
  const l = document.createElement("span");
  l.className = "plate__label";
  l.textContent = label;
  b.append(face, l);
  return b;
}
